import * as z from 'zod/mini';
import { cableIdSchema } from '../cable-id.ts';
import {
  cableTypeSchema,
  dropColorSchema,
  dropStatusSchema,
  lengthUnitSchema,
  terminationTypeSchema,
  testedOnSchema,
  testResultSchema,
} from '../cable.ts';
import { identifierSchema } from '../naming.ts';
import {
  idSchema,
  nameSchema,
  shortTextSchema,
  sortOrderSchema,
  timestampSchema,
} from './common.ts';

/** Nodes a drop may be traced back to. */
export const DROP_ORIGIN_TYPES = ['room', 'rack', 'item'] as const;
export type DropOriginType = (typeof DROP_ORIGIN_TYPES)[number];

export const dropOriginSchema = z.object({
  type: z.enum(DROP_ORIGIN_TYPES),
  id: idSchema,
  /** When the origin is a patch panel or switch: the port the run lands on. */
  port: z._default(z.nullable(z.int().check(z.positive())), null),
});
export type DropOrigin = z.infer<typeof dropOriginSchema>;

export const cableDropSchema = z.object({
  id: idSchema,
  roomId: idSchema,
  /** The cable ID (ROADMAP I7): unique in the property, never blank once saved. */
  cableId: z.string(),
  label: z.string(),
  /** Short path segment; blank only in a cache made before migration 0051. */
  identifier: z._default(z.string(), ''),
  lengthValue: z.nullable(z.number()),
  lengthUnit: lengthUnitSchema,
  cableType: cableTypeSchema,
  terminationType: terminationTypeSchema,
  fromLocationText: z.string(),
  fromNode: z.nullable(dropOriginSchema),
  /** The device (or room, or rack) the run reaches, and the port it lands on there. */
  toNode: z.nullable(dropOriginSchema),
  /** The map's colour for the run; null draws it blue. */
  color: z.nullable(dropColorSchema),
  status: dropStatusSchema,
  /** The tester's verdict: the day, the result, and the length it measured (in lengthUnit). */
  testedOn: testedOnSchema,
  testResult: testResultSchema,
  measuredLength: z.nullable(z.number()),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CableDrop = z.infer<typeof cableDropSchema>;

// The fields a person sends; the input schema fills what an older client
// leaves out, while a patch (a partial) must not, or an edit that does not
// mention a field would reset it.
const cableDropFields = {
  /** Left blank, the server fills it from the property's cable-ID sequence. */
  cableId: cableIdSchema,
  label: nameSchema,
  identifier: z.optional(identifierSchema),
  lengthValue: z.nullable(z.number().check(z.nonnegative())),
  lengthUnit: lengthUnitSchema,
  cableType: cableTypeSchema,
  terminationType: terminationTypeSchema,
  fromLocationText: shortTextSchema,
  fromNode: z.nullable(dropOriginSchema),
  toNode: z.nullable(dropOriginSchema),
  color: z.nullable(dropColorSchema),
  status: dropStatusSchema,
  testedOn: testedOnSchema,
  testResult: testResultSchema,
  measuredLength: z.nullable(z.number().check(z.nonnegative())),
};

export const cableDropInputSchema = z.object({
  ...cableDropFields,
  cableId: z._default(cableDropFields.cableId, ''),
  toNode: z._default(cableDropFields.toNode, null),
  color: z._default(cableDropFields.color, null),
  status: z._default(cableDropFields.status, 'active'),
  testedOn: z._default(cableDropFields.testedOn, null),
  testResult: z._default(cableDropFields.testResult, ''),
  measuredLength: z._default(cableDropFields.measuredLength, null),
});
export type CableDropInput = z.infer<typeof cableDropInputSchema>;

export const cableDropPatchSchema = z.partial(
  z.object({
    ...cableDropFields,
    /**
     * Request-only (ROADMAP D4): the length in this patch is the one the floor
     * plan offered — the straight line between the run's two pinned ends — and
     * the person accepted it. Nothing about it is stored on the run; it decides
     * which history row the write gets, so the record says where the number
     * came from rather than reading like a measured length.
     */
    lengthFromPlan: z.boolean(),
  }),
);
export type CableDropPatch = z.infer<typeof cableDropPatchSchema>;
